import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { env } from '../config/environment';

interface UserInfo {
  userId: string;
  userDetails: string;
  identityProvider: string;
  userRoles: string[];
  claims?: any[];
}

interface AuthContextType {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  userEmail: string | null;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
}

// Authorization configuration from environment variables
// This allows admin emails to be managed through environment configuration
// rather than hardcoded in the source code
const getAuthorizedAdmins = (): string[] => {
  return env.authorizedAdmins;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await fetch('/.auth/me');
      const data = await response.json();
      
      if (data.clientPrincipal) {
        setUser(data.clientPrincipal);
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Extract user email from authentication data
  const userEmail = user?.userDetails || null;
  
  // Check if user is an authorized admin
  const authorizedAdmins = getAuthorizedAdmins();
  const isAdmin = userEmail ? authorizedAdmins.includes(userEmail.toLowerCase()) : false;

  const login = () => {
    // Redirect to Azure AD login
    window.location.href = '/.auth/login/aad';
  };

  const logout = () => {
    // Redirect to logout endpoint
    window.location.href = '/.auth/logout';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isAdmin,
        userEmail,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
